"use client"

import { getFormByClientId } from "../API"
import { createContext, useContext, useState, useEffect, ReactNode } from "react"

// Tipo genérico para item editável
export interface ConfigItem {
  id: string
  label: Record<string, string>
  description?: Record<string, string>
  image?: string
  svgIcon?: string
  price?: number
  order: number
  active: boolean
}

// Tipo para configuração de subcategorias de esporte
export type SportSubcategory = 'onField' | 'offField' | 'collarStyles' | 'sleeveStyles' | 'crestStyles' | 'fitStyles' | 'fabricTypes' | 'templates'

// Configuração do header
export interface SocialLink {
  id: string
  platform: 'instagram' | 'facebook' | 'twitter' | 'tiktok' | 'youtube' | 'linkedin' | 'custom'
  url: string
  active: boolean
}

export interface NavLink {
  id: string
  label: Record<string, string>
  url: string
  active: boolean
}

export interface HeaderConfig {
  brandColor: string    // hex, ex: '#f75555'
  formBgType: 'default' | 'color' | 'image'  // visual de fundo do formulário
  formBgColor: string   // hex quando type=color
  formBgImage: string   // base64/URL quando type=image
  formTextColor: string // cor base dos textos do formulário
  logoType: 'svg' | 'image' | 'text'
  logoSvg: string       // SVG markup inline
  logoImage: string     // base64 or URL
  logoText: string      // fallback text
  currencyLabel: string // ex: "Brasil (BRL R$)"
  socialLinks: SocialLink[]
  navLinks: NavLink[]
}

// Configuração do footer
export interface FooterLink {
  id: string
  label: Record<string, string>
  url: string
  active: boolean
}

export interface FooterColumn {
  id: string
  title: Record<string, string>
  links: FooterLink[]
  active: boolean
}

export interface FooterConfig {
  active: boolean
  bgColor: string          // cor de fundo
  textColor: string        // cor dos textos
  accentColor: string      // cor dos links/destaques (herda brandColor por padrão)
  tagline: Record<string, string>  // slogan abaixo do logo
  email: string
  phone: string
  address: string
  columns: FooterColumn[]
  showSocials: boolean     // reutiliza socialLinks do header
  copyrightText: string
}

// Tipo para a configuração completa da loja
export interface StoreConfig {
  profiles: ConfigItem[]
  sports: ConfigItem[]
  quantities: ConfigItem[]
  urgency: ConfigItem[]
  prices: ConfigItem[]
  sportItems: Record<string, Record<SportSubcategory, ConfigItem[]>>
  header: HeaderConfig
  footer: FooterConfig
}

// Configuração padrão inicial
const defaultConfig: StoreConfig = {
  profiles: [
    { id: 'team', label: { 'pt-BR': 'Time/Clube', 'en': 'Team/Club', 'es': 'Equipo/Club' }, description: { 'pt-BR': 'Uniforme para equipe esportiva', 'en': 'Uniform for sports team', 'es': 'Uniforme para equipo deportivo' }, order: 0, active: true },
    { id: 'company', label: { 'pt-BR': 'Empresa', 'en': 'Company', 'es': 'Empresa' }, description: { 'pt-BR': 'Uniforme corporativo', 'en': 'Corporate uniform', 'es': 'Uniforme corporativo' }, order: 1, active: true },
    { id: 'event', label: { 'pt-BR': 'Evento', 'en': 'Event', 'es': 'Evento' }, description: { 'pt-BR': 'Uniforme para evento', 'en': 'Event uniform', 'es': 'Uniforme para evento' }, order: 2, active: true },
    { id: 'reseller', label: { 'pt-BR': 'Revenda', 'en': 'Reseller', 'es': 'Reventa' }, description: { 'pt-BR': 'Compra para revender', 'en': 'Purchase to resell', 'es': 'Compra para revender' }, order: 3, active: true },
  ],
  sports: [
    { id: 'soccer', label: { 'pt-BR': 'Futebol', 'en': 'Soccer', 'es': 'Fútbol' }, order: 0, active: true },
    { id: 'basketball', label: { 'pt-BR': 'Basquete', 'en': 'Basketball', 'es': 'Baloncesto' }, order: 1, active: true },
    { id: 'baseball', label: { 'pt-BR': 'Beisebol', 'en': 'Baseball', 'es': 'Béisbol' }, order: 2, active: true },
    { id: 'hockey', label: { 'pt-BR': 'Hóquei', 'en': 'Hockey', 'es': 'Hockey' }, order: 3, active: true },
    { id: 'football', label: { 'pt-BR': 'Futebol Americano', 'en': 'Football', 'es': 'Fútbol Americano' }, order: 4, active: true },
    { id: 'lacrosse', label: { 'pt-BR': 'Lacrosse', 'en': 'Lacrosse', 'es': 'Lacrosse' }, order: 5, active: true },
    { id: 'rugby', label: { 'pt-BR': 'Rugby', 'en': 'Rugby', 'es': 'Rugby' }, order: 6, active: true },
    { id: 'track', label: { 'pt-BR': 'Atletismo', 'en': 'Track', 'es': 'Atletismo' }, order: 7, active: true },
    { id: 'esports', label: { 'pt-BR': 'E-sports', 'en': 'Esports', 'es': 'E-sports' }, order: 8, active: true },
  ],
  quantities: [
    { id: '10-19', label: { 'pt-BR': '10-19', 'en': '10-19', 'es': '10-19' }, description: { 'pt-BR': 'Pedido mínimo', 'en': 'Minimum order', 'es': 'Pedido mínimo' }, order: 0, active: true },
    { id: '20-29', label: { 'pt-BR': '20-29', 'en': '20-29', 'es': '20-29' }, order: 1, active: true },
    { id: '30-49', label: { 'pt-BR': '30-49', 'en': '30-49', 'es': '30-49' }, description: { 'pt-BR': 'Melhor custo-benefício', 'en': 'Best value', 'es': 'Mejor costo-beneficio' }, order: 2, active: true },
    { id: '50-99', label: { 'pt-BR': '50-99', 'en': '50-99', 'es': '50-99' }, description: { 'pt-BR': '15% desconto', 'en': '15% discount', 'es': '15% descuento' }, price: -15, order: 3, active: true },
    { id: '100+', label: { 'pt-BR': '100+', 'en': '100+', 'es': '100+' }, description: { 'pt-BR': '20% desconto', 'en': '20% discount', 'es': '20% descuento' }, price: -20, order: 4, active: true },
  ],
  urgency: [
    { id: 'normal', label: { 'pt-BR': 'Normal', 'en': 'Normal', 'es': 'Normal' }, description: { 'pt-BR': '4-6 semanas', 'en': '4-6 weeks', 'es': '4-6 semanas' }, order: 0, active: true },
    { id: 'urgent', label: { 'pt-BR': 'Urgente', 'en': 'Urgent', 'es': 'Urgente' }, description: { 'pt-BR': '2-3 semanas (+25%)', 'en': '2-3 weeks (+25%)', 'es': '2-3 semanas (+25%)' }, price: 25, order: 1, active: true },
  ],
  prices: [
    { id: 'basePrice', label: { 'pt-BR': 'Preço Base por Peça', 'en': 'Base Price per Piece', 'es': 'Precio Base por Pieza' }, price: 89, order: 0, active: true },
  ],
  header: {
    brandColor: '#f75555',
    formBgType: 'default',
    formBgColor: '#f0f4f8',
    formBgImage: '',
    formTextColor: '#111111',
    logoType: 'svg',
    logoSvg: '',
    logoImage: '',
    logoText: 'JERSEYBIRD',
    currencyLabel: 'Brasil (BRL R$)',
    socialLinks: [
      { id: 'instagram', platform: 'instagram', url: '#', active: true },
      { id: 'facebook', platform: 'facebook', url: '#', active: true },
      { id: 'twitter', platform: 'twitter', url: '#', active: true },
      { id: 'tiktok', platform: 'tiktok', url: '#', active: true },
    ],
    navLinks: [
      { id: 'startProject', label: { 'pt-BR': 'Iniciar Projeto', 'en': 'Start a Project', 'es': 'Iniciar Proyecto' }, url: '#', active: true },
      { id: 'aboutUs', label: { 'pt-BR': 'Sobre Nós', 'en': 'About Us', 'es': 'Sobre Nosotros' }, url: '#', active: true },
      { id: 'findYourSport', label: { 'pt-BR': 'Encontre seu Esporte', 'en': 'Find Your Sport', 'es': 'Encuentra tu Deporte' }, url: '#', active: true },
      { id: 'shop', label: { 'pt-BR': 'Loja', 'en': 'Shop', 'es': 'Tienda' }, url: '#', active: true },
    ],
  },
  footer: {
    active: true,
    bgColor: '#111111',
    textColor: '#ffffff',
    accentColor: '',
    tagline: { 'pt-BR': 'Uniformes esportivos personalizados.', 'en': 'Custom sports uniforms.', 'es': 'Uniformes deportivos personalizados.' },
    email: '',
    phone: '',
    address: '',
    showSocials: true,
    copyrightText: '',
    columns: [
      {
        id: 'company',
        title: { 'pt-BR': 'Empresa', 'en': 'Company', 'es': 'Empresa' },
        active: true,
        links: [
          { id: 'about', label: { 'pt-BR': 'Sobre nós', 'en': 'About us', 'es': 'Sobre nosotros' }, url: '#', active: true },
          { id: 'contact', label: { 'pt-BR': 'Contato', 'en': 'Contact', 'es': 'Contacto' }, url: '#', active: true },
        ],
      },
      {
        id: 'sports',
        title: { 'pt-BR': 'Esportes', 'en': 'Sports', 'es': 'Deportes' },
        active: true,
        links: [
          { id: 'soccer', label: { 'pt-BR': 'Futebol', 'en': 'Soccer', 'es': 'Fútbol' }, url: '#', active: true },
          { id: 'basketball', label: { 'pt-BR': 'Basquete', 'en': 'Basketball', 'es': 'Baloncesto' }, url: '#', active: true },
        ],
      },
    ],
  },
  sportItems: {
    soccer: {
      onField: [
        { id: 'homeShirt', label: { 'pt-BR': 'Camisa Titular', 'en': 'Home Shirt', 'es': 'Camiseta Local' }, order: 0, active: true },
        { id: 'homeShorts', label: { 'pt-BR': 'Shorts Titular', 'en': 'Home Shorts', 'es': 'Shorts Local' }, order: 1, active: true },
        { id: 'homeSocks', label: { 'pt-BR': 'Meias Titular', 'en': 'Home Socks', 'es': 'Medias Local' }, order: 2, active: true },
        { id: 'awayShirt', label: { 'pt-BR': 'Camisa Reserva', 'en': 'Away Shirt', 'es': 'Camiseta Visitante' }, order: 3, active: true },
        { id: 'awayShorts', label: { 'pt-BR': 'Shorts Reserva', 'en': 'Away Shorts', 'es': 'Shorts Visitante' }, order: 4, active: true },
        { id: 'awaySocks', label: { 'pt-BR': 'Meias Reserva', 'en': 'Away Socks', 'es': 'Medias Visitante' }, order: 5, active: true },
      ],
      offField: [
        { id: 'trainingJacket', label: { 'pt-BR': 'Jaqueta de Treino', 'en': 'Training Jacket', 'es': 'Chaqueta de Entrenamiento' }, order: 0, active: true },
        { id: 'hoodie', label: { 'pt-BR': 'Moletom', 'en': 'Hoodie', 'es': 'Sudadera' }, order: 1, active: true },
        { id: 'polo', label: { 'pt-BR': 'Polo', 'en': 'Polo', 'es': 'Polo' }, order: 2, active: true },
      ],
      collarStyles: [
        { id: 'crewneck', label: { 'pt-BR': 'Gola Careca', 'en': 'Crewneck', 'es': 'Cuello Redondo' }, order: 0, active: true },
        { id: 'v-neck', label: { 'pt-BR': 'Gola V', 'en': 'V-Neck', 'es': 'Cuello V' }, order: 1, active: true },
        { id: 'polo', label: { 'pt-BR': 'Polo', 'en': 'Polo', 'es': 'Polo' }, price: 5, order: 2, active: true },
      ],
      sleeveStyles: [
        { id: 'set-in', label: { 'pt-BR': 'Manga Tradicional', 'en': 'Set-in Sleeves', 'es': 'Manga Tradicional' }, order: 0, active: true },
        { id: 'raglan', label: { 'pt-BR': 'Manga Raglan', 'en': 'Raglan Sleeves', 'es': 'Manga Raglan' }, order: 1, active: true },
      ],
      crestStyles: [
        { id: 'sublimated', label: { 'pt-BR': 'Escudo Sublimado', 'en': 'Sublimated Crest', 'es': 'Escudo Sublimado' }, order: 0, active: true },
        { id: 'silicone', label: { 'pt-BR': 'Escudo em Silicone', 'en': 'Silicone Crest', 'es': 'Escudo en Silicona' }, price: 9, order: 1, active: true },
        { id: 'embroidered', label: { 'pt-BR': 'Escudo Bordado', 'en': 'Embroidered Crest', 'es': 'Escudo Bordado' }, price: 12, order: 2, active: true },
      ],
      fitStyles: [
        { id: 'pro', label: { 'pt-BR': 'Ajuste Pro', 'en': 'Pro Fit', 'es': 'Ajuste Pro' }, order: 0, active: true },
        { id: 'casual', label: { 'pt-BR': 'Ajuste Casual', 'en': 'Casual Fit', 'es': 'Ajuste Casual' }, order: 1, active: true },
      ],
      fabricTypes: [
        { id: 'standard', label: { 'pt-BR': 'Tecido Padrão', 'en': 'Standard Fabric', 'es': 'Tela Estándar' }, order: 0, active: true },
        { id: 'pro', label: { 'pt-BR': 'Tecido Pro', 'en': 'Pro Fabric', 'es': 'Tela Pro' }, price: 10, order: 1, active: true },
        { id: 'elite', label: { 'pt-BR': 'Tecido Elite', 'en': 'Elite Fabric', 'es': 'Tela Elite' }, price: 20, order: 2, active: true },
      ],
      templates: [
        { id: 'blank', label: { 'pt-BR': 'Em Branco', 'en': 'Blank', 'es': 'En Blanco' }, order: 0, active: true },
        { id: 'stripes', label: { 'pt-BR': 'Listras', 'en': 'Stripes', 'es': 'Rayas' }, order: 1, active: true },
        { id: 'gradient', label: { 'pt-BR': 'Degradê', 'en': 'Gradient', 'es': 'Degradado' }, order: 2, active: true },
      ],
    },
  },
}

// Context
interface StoreConfigContextType {
  config: StoreConfig
  updateItem: (category: keyof StoreConfig, items: ConfigItem[]) => void
  updateSportItems: (sportId: string, subcategory: SportSubcategory, items: ConfigItem[]) => void
  resetConfig: () => void
  populateConfig: () => void
  updateConfig: (config: StoreConfig) => void
  updateHeader: (header: HeaderConfig) => void
  updateFooter: (footer: FooterConfig) => void
  getActiveItems: (category: 'profiles' | 'sports' | 'quantities' | 'urgency' | 'prices') => ConfigItem[]
  getActiveSportItems: (sportId: string, subcategory: SportSubcategory) => ConfigItem[]
}

const StoreConfigContext = createContext<StoreConfigContextType | null>(null)

const emptyConfig: StoreConfig = {
  profiles: [],
  sports: [],
  quantities: [],
  urgency: [],
  prices: [],
  sportItems: {},
  header: {
    brandColor: '#f75555',
    formBgType: 'default',
    formBgColor: '',
    formBgImage: '',
    formTextColor: '#111111',
    logoType: 'text',
    logoSvg: '',
    logoImage: '',
    logoText: '',
    currencyLabel: '',
    socialLinks: [],
    navLinks: [],
  },
  footer: {
    active: false,
    bgColor: '#111111',
    textColor: '#ffffff',
    accentColor: '',
    tagline: { 'pt-BR': '', 'en': '', 'es': '' },
    email: '',
    phone: '',
    address: '',
    showSocials: false,
    copyrightText: '',
    columns: [],
  },
}


const STORAGE_KEY = 'store-config'

export function StoreConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<StoreConfig>(defaultConfig)
  const [isLoaded, setIsLoaded] = useState(false)

  // Carregar do localStorage
  useEffect(() => {
    async function load() {
      const stored = localStorage.getItem(STORAGE_KEY)

      loadForm(localStorage.getItem("user_id"), false);

      setIsLoaded(true)
    }

    load();
  }, []);

  const findById = <T extends { id: string }>(
    arr: T[],
    id: string
  ): T | undefined => {
    return arr.find(item => item.id === id);
  };

  async function loadForm(id: any, isIframe: boolean) {
    // Lógica para carregar o formulário, se necessário
    try {
      const result = await getFormByClientId(id);

      if (!result) return;

      const parsed = result; // 👈 AQUI

      if (isIframe) {

        /*const instagram = findById(parsed.header.socialLinks, "instagram") as any;

        if (instagram) {
          $("#instagram-iframe").attr("href", instagram.url);
        } else {
          $("#instagram-iframe").remove();
        }

        const facebook = parsed.header.navLinks[0] as any;

        if (facebook) {
          $("#facebook-iframe").attr("href", facebook.url);
        } else {
          $("#facebook-iframe").remove();
        }

        const site = findById(parsed.header.socialLinks, "facebook") as any;

        if (facebook) {
          $("#site-iframe").attr("href", facebook.url);
        } else {
          $("#site-iframe").remove();
        }

        $("#links-iframe").empty();

        for (var i = 0; i < parsed.header.navLinks.length; i++) {
          const link = parsed.header.navLinks[i] as any;



          const elem = '<a target="_blank" href="'+link.url+'" className="hover:opacity-80 transition-opacity">'+link.label['pt-BR']+'</a>'

          $("#links-iframe").append(elem);
        }
*/

      }

      setConfig({
        ...defaultConfig,
        ...parsed,
        header: {
          ...defaultConfig.header,
          ...(parsed.header ?? {})
        },
        footer: {
          ...defaultConfig.footer,
          ...(parsed.footer ?? {})
        }
      });

    } catch (e) {
      console.error('Failed to parse stored config:', e)
    }
  }

  (window as any)["loadForm"] = async function (id: any) {
    loadForm(id, true);
  }

  // Salvar no localStorage
  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config))
    }
  }, [config, isLoaded])

  const updateItem = (category: keyof StoreConfig, items: ConfigItem[]) => {
    setConfig(prev => ({
      ...prev,
      [category]: items,
    }))
  }

  const resetConfig = () => {
    setConfig(emptyConfig)
    localStorage.removeItem(STORAGE_KEY)
  }

  const populateConfig = () => {
    setConfig(defaultConfig)
  }

  const updateConfig = (newConfig: StoreConfig) => {
    setConfig({ ...defaultConfig, ...newConfig, header: { ...defaultConfig.header, ...(newConfig.header ?? {}) }, footer: { ...defaultConfig.footer, ...(newConfig.footer ?? {}) } })
  }

  const updateFooter = (footer: FooterConfig) => {
    setConfig(prev => ({ ...prev, footer }))
  }

  const updateHeader = (header: HeaderConfig) => {
    setConfig(prev => ({ ...prev, header }))
  }

  const updateSportItems = (sportId: string, subcategory: SportSubcategory, items: ConfigItem[]) => {
    setConfig(prev => ({
      ...prev,
      footer: {
        active: true,
        bgColor: '#111111',
        textColor: '#ffffff',
        accentColor: '',
        tagline: { 'pt-BR': 'Uniformes esportivos personalizados.', 'en': 'Custom sports uniforms.', 'es': 'Uniformes deportivos personalizados.' },
        email: '',
        phone: '',
        address: '',
        showSocials: true,
        copyrightText: '',
        columns: [
          {
            id: 'company',
            title: { 'pt-BR': 'Empresa', 'en': 'Company', 'es': 'Empresa' },
            active: true,
            links: [
              { id: 'about', label: { 'pt-BR': 'Sobre nós', 'en': 'About us', 'es': 'Sobre nosotros' }, url: '#', active: true },
              { id: 'contact', label: { 'pt-BR': 'Contato', 'en': 'Contact', 'es': 'Contacto' }, url: '#', active: true },
            ],
          },
          {
            id: 'sports',
            title: { 'pt-BR': 'Esportes', 'en': 'Sports', 'es': 'Deportes' },
            active: true,
            links: [
              { id: 'soccer', label: { 'pt-BR': 'Futebol', 'en': 'Soccer', 'es': 'Fútbol' }, url: '#', active: true },
              { id: 'basketball', label: { 'pt-BR': 'Basquete', 'en': 'Basketball', 'es': 'Baloncesto' }, url: '#', active: true },
            ],
          },
        ],
      },
      sportItems: {
        ...prev.sportItems,
        [sportId]: {
          ...(prev.sportItems[sportId] || {}),
          [subcategory]: items,
        },
      },
    }))
  }

  const getActiveItems = (category: 'profiles' | 'sports' | 'quantities' | 'urgency' | 'prices') => {
    return (config[category] || [])
      .filter(item => item.active)
      .sort((a, b) => a.order - b.order)
  }

  const getActiveSportItems = (sportId: string, subcategory: SportSubcategory) => {
    return (config.sportItems[sportId]?.[subcategory] || [])
      .filter(item => item.active)
      .sort((a, b) => a.order - b.order)
  }

  return (
    <StoreConfigContext.Provider value={{
      config,
      updateConfig,
      updateItem,
      resetConfig,
      populateConfig,
      updateHeader,
      updateFooter,
      updateSportItems,
      getActiveItems,
      getActiveSportItems,
    }}>
      {children}
    </StoreConfigContext.Provider>
  )
}

export function useStoreConfig() {
  const context = useContext(StoreConfigContext)
  if (!context) {
    throw new Error('useStoreConfig must be used within a StoreConfigProvider')
  }
  return context
}